'use client';

import { Grid, Loader } from '@coolblue-development/freddy';
import addAccessoryToCartTrigger from '@coolblue-development/ga4-events/triggers/OptionsRow/addAccessoryToCartTrigger';
import changeQuantityTrigger from '@coolblue-development/ga4-events/triggers/OptionsRow/changeQuantityTrigger';
import deselectTrigger from '@coolblue-development/ga4-events/triggers/OptionsRow/deselectTrigger';
import selectNoAccessoryTrigger from '@coolblue-development/ga4-events/triggers/OptionsRow/selectNoAccessoryTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { SelectableBoxGroup } from '@coolblue-development/next-patterns/client';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import { AccessoryProductCardProvider } from '@features/add-to-cart-page/components/Sections/AccessoryProductCard/AccessoryProductCard.context';
import { AddToCartPageType } from '@features/add-to-cart-page/typings/AddToCartPageSection';
import { Product } from '@features/add-to-cart-page/typings/Product';
import { updateShoppingCart } from '@utils/nextShoppingCartActions/updateShoppingCart';
import { debounceCallback } from '@utils/utils';
import { useMemo, useRef, useState } from 'react';

import { mapProductToSummaryItem, useSummaryStore } from '../../SummaryStore';
import { useCustomerContext } from '../CustomerContext/CustomerContext';
import DefaultOptionCard from './DefaultOptionCard/DefaultOptionCard';
import type { ProductOptionCardProps } from './ProductOptionCard/ProductOptionCard';
import ProductOptionCard from './ProductOptionCard/ProductOptionCard';
type Props = {
  slots: ProductOptionCardProps[];
  mainProductId: number;
  addToCartPageType: AddToCartPageType;
};

const SELECTABLE_BOX_GROUP_NAME = 'optionsRow';

const OptionsRow = ({ slots, mainProductId, addToCartPageType }: Props) => {
  const { isBusinessCustomer } = useCustomerContext();
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState('default');
  const products = useMemo(() => mapSlotsToProducts(slots), [slots]);
  const { isInternalUser } = useRequestContext();
  const { trackEvent } = useAnalytics();

  const { addItem, removeItem, mainProductQuantity } = useSummaryStore();

  const gridRef = useRef<HTMLInputElement>(null);

  const quantitiesRef = useRef<Record<number, number>>({});

  function getProductFromOption(value: string) {
    if (value === 'default') return undefined;

    const id = Number(value);
    return products.get(id);
  }

  function getOptionIndex(optionValue: string) {
    if (optionValue === 'default' || !gridRef.current) return 1;

    const $inputs = gridRef.current.querySelectorAll<HTMLInputElement>(
      `input[name="${SELECTABLE_BOX_GROUP_NAME}"]`
    );

    // Index passed to the ProductCard is 1-based
    return Array.from($inputs).findIndex($input => $input.value === optionValue) + 1;
  }

  function getOptionQuantity(option: string): number {
    const optionQuantityLimit = getProductFromOption(option)?.orderQuantityLimit;
    if (!isBusinessCustomer || !mainProductQuantity) return 1;

    return optionQuantityLimit && optionQuantityLimit < mainProductQuantity
      ? optionQuantityLimit
      : mainProductQuantity;
  }

  function findIndex(productId: number) {
    let index = slots.findIndex(slot => slot.slotProducts?.[0]?.id === productId);
    if (index === -1) {
      index = slots.findIndex(slot =>
        slot.slotProducts?.[0]?.colorAlternatives?.some(
          alternative => alternative.product?.id === productId
        )
      );
    }

    return index;
  }

  function trackChange(value: string) {
    const prevOption = selectedOption;
    const product = getProductFromOption(value);

    if (!product) {
      trackEvent(
        selectNoAccessoryTrigger({
          sub_feature: addToCartPageType,
          index: 1,
          total_indices: slots.length
        })
      );

      return;
    }

    const index = findIndex(product.id) + 1;
    const prevIndex = prevOption === 'default' ? 1 : findIndex(+prevOption) + 1;

    if (prevIndex > 1 && prevIndex !== index) {
      trackEvent(
        deselectTrigger({
          sub_feature: addToCartPageType,
          total_indices: slots.length,
          index: prevIndex,
          item_id: +prevOption
        })
      );
    }

    trackEvent(
      addAccessoryToCartTrigger({
        sub_feature: addToCartPageType,
        primary_item_id: mainProductId,
        attached_item_id: product.id,
        total_indices: slots.length,
        index
      })
    );
  }

  async function handleChange(newOption: string) {
    if (loading || selectedOption === newOption) return;

    const newOptionIndex = getOptionIndex(newOption);

    const quantity = quantitiesRef.current[newOptionIndex] ?? getOptionQuantity(newOption);

    await selectOption(newOption, { quantity });
  }

  const debouncedHandleQuantityChange = debounceCallback(
    async ({ productId, value }: { productId: number; value: number }) => {
      await selectOption(productId.toString(), { quantity: value });
    },
    1000
  );

  function getQuantityStepperConfig() {
    if (!isBusinessCustomer) return undefined;

    return {
      onChange: ({
        index,
        productId,
        value,
        shouldDebounce = true
      }: {
        index: number;
        productId: number;
        value: number;
        shouldDebounce?: boolean;
      }) => {
        const previousQuantity =
          quantitiesRef.current[index] || getOptionQuantity(productId.toString());
        quantitiesRef.current[index] = value;

        if (shouldDebounce) debouncedHandleQuantityChange({ productId, value });

        trackEvent(
          changeQuantityTrigger({
            sub_feature: 'options_row',
            item_id: productId,
            type: previousQuantity < value ? 'add' : 'remove',
            quantity: value
          })
        );
      },
      getQuantity: (index: number) => quantitiesRef.current[index]
    };
  }

  async function selectOption(newOption: string, { quantity }: { quantity: number }) {
    const currentOption = selectedOption;
    const currentProduct = getProductFromOption(currentOption);
    const newProduct = getProductFromOption(newOption);

    setLoading(true);
    setSelectedOption(newOption);

    try {
      await updateShoppingCart({
        remove: currentProduct?.id ? { productId: currentProduct?.id } : undefined,
        add: newProduct?.id ? { productId: newProduct?.id, quantity } : undefined
      });

      if (currentProduct?.id) {
        removeItem(currentProduct?.id);
      }

      if (newProduct) {
        addItem(mapProductToSummaryItem(newProduct, quantity));
      }

      if (currentOption !== newOption) {
        trackChange(newOption);
      }

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      // TODO: Maybe show a toast here with a user friendly error message;
      setSelectedOption(currentOption);
    } finally {
      setLoading(false);
    }
  }

  return (
    slots.length > 0 && (
      <Grid
        mt={400}
        rowGap={{ xs: 400, md: 0 }}
        columnGap={{ xs: 0, md: 400 }}
        sx={{ position: 'relative' }}
        ref={gridRef}
      >
        {loading && <Loader data-cy={isInternalUser ? 'options-row-loader' : undefined} />}
        <SelectableBoxGroup
          inputPlacement='left'
          name={SELECTABLE_BOX_GROUP_NAME}
          spacing='default'
          type='radio'
          onChange={handleChange}
          value={selectedOption}
        >
          {slots.map((slot, index) =>
            slot.slotProducts?.length > 0 ? (
              <AccessoryProductCardProvider
                key={slot.slotProducts[0].id}
                initialProduct={slot.slotProducts[0]}
                initialQuantity={mainProductQuantity}
              >
                <ProductOptionCard
                  {...slot}
                  disabled={loading}
                  mainProductId={mainProductId}
                  index={index + 1}
                  totalIndices={slots.length}
                  handleColorChange={handleChange}
                  quantityStepper={getQuantityStepperConfig()}
                  selectedOption={selectedOption}
                />
              </AccessoryProductCardProvider>
            ) : (
              <DefaultOptionCard {...slot} key='default' disabled={loading} />
            )
          )}
        </SelectableBoxGroup>
      </Grid>
    )
  );
};

function mapSlotsToProducts(slots: ProductOptionCardProps[]) {
  return slots.reduce((acc, slot) => {
    if (slot.slotProducts?.length > 0) {
      acc.set(slot.slotProducts[0].id, slot.slotProducts[0]);

      slot.slotProducts[0].colorAlternatives?.forEach(alternative => {
        if (alternative.product?.id && alternative.product.id !== slot.slotProducts[0].id) {
          acc.set(alternative.product.id, alternative.product);
        }
      });
    }

    return acc;
  }, new Map<Product['id'], Product>());
}

export default OptionsRow;
